import { PlusOutlined, ScanOutlined } from "@ant-design/icons";
import { Button, Col, Input, Modal, Row } from "antd";
import React, { useEffect, useState } from "react";
import { QrReader } from "react-qr-reader";
import { useSearchParams } from "react-router-dom";
import {
	getCompleteInvoice,
	getCreditMemosForInvoice,
	getInvoiceByNo,
} from "../../services/invoiceService";
import { getFinalDocumentNo } from "../../services/invoiceSyncService";
import { getInvoicePaymentLines } from "../../services/invoicePaymentService";
import InvoiceForm from "./InvoiceForm";
import InvoiceList from "./InvoiceList";
import { getInvoiceListConfig } from "./configs/invoiceList.config";
import { useConfig } from "../../contexts/ConfigContext";
import { isEmpty, formatAxiosError } from "../../utils/common";
import "./InvoiceStyles.css";

const InvoiceManagement = ({ documentType = "Invoice", syncStatus = "" }) => {
	const { country } = useConfig();
	const { showNotasCredito } = getInvoiceListConfig(country?.code);
	const [showForm, setShowForm] = useState(false);
	const [selectedInvoice, setSelectedInvoice] = useState(null);
	const [qrModalVisible, setQrModalVisible] = useState(false);
	const [invoiceNo, setInvoiceNo] = useState("");
	const [cameraError, setCameraError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [errorCount, setErrorCount] = useState(0);
	const [isQrReaderActive, setIsQrReaderActive] = useState(false);
	const [searchParams] = useSearchParams();

	// Obtener filtro de fecha desde los query params
	const dateFilter = searchParams.get("filter");

	// Determinar si estamos en modo facturas o abonos
	const isInvoiceMode = documentType === "Invoice";
	const isCreditMemoMode = documentType === "Credit Memo";
	const isPendingMode = syncStatus === "pending";

	// Detener el escáner QR cuando showForm cambia
	useEffect(() => {
		if (showForm) {
			setQrModalVisible(false);
			setIsQrReaderActive(false);
		}
	}, [showForm]);

	const handleAddInvoice = () => {
		// Si estamos en modo abonos, crear directamente un abono
		if (isCreditMemoMode) {
			setSelectedInvoice({
				Document_Type: "Credit Memo",
			});
		} else {
			setSelectedInvoice(null);
		}
		setShowForm(true);
	};

	const handleEditInvoice = (invoice) => {
		setSelectedInvoice(invoice);
		setShowForm(true);
	};

	const handleInvoiceSaved = () => {
		setShowForm(false);
	};

	const handleCancel = () => {
		setShowForm(false);
	};

	const handleShowCreditMemoModal = () => {
		setQrModalVisible(true);
		setInvoiceNo("");
		setCameraError(false);
		setErrorCount(0);
		setIsQrReaderActive(true);
	};

	const handleQrScan = async (result, error) => {
		// Si el escáner no está activo, no procesar nada
		if (!isQrReaderActive) return;

		if (error) {
			setErrorCount((prev) => {
				const newCount = prev + 1;
				// Si hay más de 5 errores en secuencia, considerar que la cámara no funciona
				if (newCount > 5 && !cameraError) {
					console.error(
						"Demasiados errores de cámara, deshabilitando escáner QR"
					);
					setCameraError(true);
					window.messageApi.error(
						"Cámara no disponible o con problemas. Por favor, use la entrada manual."
					);
				}
				return newCount;
			});
			return;
		}

		// Resetear contador de errores si se recibe un resultado exitoso
		if (errorCount > 0) {
			setErrorCount(0);
		}

		if (result && !loading) {
			const data = result?.text;
			setLoading(true);
			try {
				await processInvoiceNo(data);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleManualInput = async () => {
		if (!invoiceNo.trim()) {
			window.messageApi.error("Por favor, introduzca un número de factura");
			return;
		}

		setLoading(true);
		try {
			await processInvoiceNo(invoiceNo);
		} finally {
			setLoading(false);
		}
	};

	const processInvoiceNo = async (no) => {
		try {
			let invoiceInput = no.trim();
			let invoice = null;

			// Eliminar ceros a la izquierda si es un número
			const cleanedInput = invoiceInput.replace(/^0+/, "");

			// Intentar buscar primero por ID (asumiendo que es un número)
			if (!isNaN(cleanedInput) && cleanedInput !== "") {
				const invoiceId = parseInt(cleanedInput, 10);

				// Intentar encontrar la factura por ID
				try {
					invoice = await getCompleteInvoice(invoiceId);
				} catch (error) {
					console.log(
						"No se encontró la factura por ID, intentando por número de factura:",
						error
					);
				}
			}

			// Si no se encontró por ID, buscar por número de factura
			if (!invoice) {
				invoice = await getInvoiceByNo(invoiceInput);
			}

			if (!invoice) {
				window.messageApi.error(
					`No se encontró ninguna factura con el número o ID ${invoiceInput}`
				);
				return;
			}

			// Verificar que la factura sea de tipo Invoice, no Credit Memo
			if (invoice.Document_Type !== "Invoice") {
				window.messageApi.error(
					"Solo se pueden crear notas de crédito a partir de facturas, no de otras notas de crédito"
				);
				return;
			}

			//Validar que la factura este contabilizada (posted)
			await getFinalDocumentNo(invoice, country);

			invoice = await getCompleteInvoice(invoice.id); //recupero la información actualizada

			if (isEmpty(invoice.Pre_Assigned_No)) {
				window.messageApi.error(
					"Para realizar la nota de crédito la factura debe estar contabilizada."
				);
				return;
			}

			// Si la factura se encontró por No, obtener factura completa con líneas
			let completeInvoice = invoice;
			if (!invoice.lines) {
				completeInvoice = await getCompleteInvoice(invoice.id);
			}

			if (!completeInvoice) {
				window.messageApi.error(
					"No se pudo obtener la información completa de la factura"
				);
				return;
			}

			if (!completeInvoice.lines || completeInvoice.lines.length === 0) {
				window.messageApi.error("La factura no tiene líneas para abonar");
				return;
			}

			// Obtener las líneas de pago de la factura original
			let paymentLines = [];
			let hadMultiplePaymentMethods = false;
			try {
				paymentLines = await getInvoicePaymentLines(completeInvoice);
				// Añadir las líneas de pago a la factura completa para tenerlas disponibles
				completeInvoice.paymentLines = paymentLines;
				// Verificar si la factura original tenía múltiples formas de pago
				hadMultiplePaymentMethods = paymentLines.length > 1;
			} catch (error) {
				console.error("Error al obtener líneas de pago:", error);
				window.messageApi.warning(
					"No se pudieron obtener las formas de pago originales"
				);
			}

			// Buscar todos los abonos existentes para esta factura
			let existingCreditMemos = [];
			if (completeInvoice.No) {
				existingCreditMemos = await getCreditMemosForInvoice(
					completeInvoice.No
				);
			}

			// Crear un mapa de cantidades ya abonadas por producto
			const abonedQuantities = {};

			// Procesar todos los abonos existentes para esta factura
			for (const creditMemo of existingCreditMemos) {
				// Obtener las líneas completas de este abono si no las tiene
				let creditMemoWithLines = creditMemo;
				if (!creditMemo.lines) {
					creditMemoWithLines = await getCompleteInvoice(creditMemo.id);
				}

				// Acumular las cantidades abonadas por número de línea y producto
				if (creditMemoWithLines.lines) {
					for (const line of creditMemoWithLines.lines) {
						const key = `${line.No}-${line.Description}`;
						// Las cantidades en abonos son negativas, las convertimos a positivas para acumularlas
						const abonedQuantity = Math.abs(line.Quantity || 0);

						if (!abonedQuantities[key]) {
							abonedQuantities[key] = 0;
						}
						abonedQuantities[key] += abonedQuantity;
					}
				}
			}

			// Crear líneas para el nuevo abono, teniendo en cuenta las cantidades ya abonadas
			const creditMemoLines = [];
			let hasProductsToReturn = false;

			for (const line of completeInvoice.lines) {
				const key = `${line.No}-${line.Description}`;
				const originalQuantity = Math.abs(line.Quantity || 0);
				const alreadyAbonedQuantity = abonedQuantities[key] || 0;
				const remainingQuantity = originalQuantity - alreadyAbonedQuantity;

				// Solo incluir líneas con cantidades disponibles para abonar
				if (remainingQuantity > 0) {
					hasProductsToReturn = true;
					creditMemoLines.push({
						...line,
						id: undefined, // Nueva línea
						originalQuantity: originalQuantity, // Cantidad original para referencia
						remainingQuantity: remainingQuantity, // Cantidad disponible para abonar
						alreadyAbonedQuantity: alreadyAbonedQuantity, // Cantidad ya abonada
						Quantity: -remainingQuantity, // Por defecto abonar todo lo que queda
						invoiceId: undefined, // Se asignará al guardar
					});
				}
			}

			// Verificar si queda algo por abonar
			if (!hasProductsToReturn) {
				window.messageApi.error(
					"Todos los productos de esta factura ya han sido abonados completamente"
				);
				return;
			}

			// Calcular el total exacto de productos que se van a devolver (incluyendo IVA)
			// Calcular el total exacto de productos que se van a devolver ahora (incluyendo IVA)
			let returnedSubtotal = 0;
			let returnedVat = 0;

			creditMemoLines.forEach((line) => {
				const quantity = Math.abs(line.Quantity || 0);
				const unitPrice = line.Unit_Price || 0;
				const discount = line.Line_Discount_ || 0;
				const discountAmount = unitPrice * (discount / 100);
				const finalPrice = unitPrice - discountAmount;
				const lineSubtotal = quantity * finalPrice;

				// Aplicar IVA específico del producto
				const lineIva = line.PorIva || 0;
				const lineVat = lineSubtotal * (lineIva / 100);

				returnedSubtotal += lineSubtotal;
				returnedVat += lineVat;
			});

			const returnedTotalWithVat = returnedSubtotal + returnedVat;

			// Crear líneas de pago automáticamente basadas en la factura original
			// pero con importes que sumen exactamente el total de productos devueltos (incluyendo IVA)
			const creditMemoPaymentLines = [];
			if (paymentLines && paymentLines.length > 0 && returnedTotalWithVat > 0) {
				// Calcular la suma total de las líneas de pago originales para mantener proporciones
				const originalPaymentTotal = paymentLines.reduce(
					(sum, line) => sum + Math.abs(line.Amount || 0),
					0
				);

				for (let i = 0; i < paymentLines.length; i++) {
					const originalPaymentLine = paymentLines[i];

					// Calcular la proporción que representa esta forma de pago del total original
					const originalAmount = Math.abs(originalPaymentLine.Amount || 0);
					const paymentProportion =
						originalPaymentTotal > 0
							? originalAmount / originalPaymentTotal
							: 0;

					// Aplicar esa misma proporción al total de productos devueltos (con IVA)
					let proportionalAmount = returnedTotalWithVat * paymentProportion;

					// Solo aplicar descuento si la factura original tenía UNA SOLA forma de pago
					// Si había múltiples formas de pago, no se aplicó descuento en la factura original
					const paymentDiscount = originalPaymentLine.Dto || 0;
					if (paymentDiscount > 0 && !hadMultiplePaymentMethods) {
						// Aplicar el descuento solo si era una forma de pago única en la factura original
						proportionalAmount =
							proportionalAmount * (1 - paymentDiscount / 100);
					}

					creditMemoPaymentLines.push({
						id: Date.now() + i, // ID temporal único
						Payment_Method: originalPaymentLine.Payment_Method,
						Amount: proportionalAmount, // Importe exacto proporcional (con IVA y descuento aplicado solo si corresponde)
						Dto: hadMultiplePaymentMethods ? 0 : originalPaymentLine.Dto || 0, // Guardar descuento solo si había una sola forma de pago
						Line_No: i + 1,
						// Marcar como línea automática para evitar edición manual
						isAutoGenerated: true,
						originalPaymentLineId: originalPaymentLine.id,
						// Guardar información original para recálculos posteriores
						originalAmount: originalAmount,
						paymentProportion: paymentProportion,
					});
				}
			}

			// Crear una nueva factura basada en la original pero con tipo "Credit Memo"
			const creditMemo = {
				...completeInvoice,
				id: undefined, // Crear nueva factura
				No: undefined, // No sincronizada aún
				Document_Type: "Credit Memo",
				lastSynced: null,
				pendingSync: 1,
				// Usar fechas actuales para la nota de crédito, no las de la factura original
				Posting_Date: new Date().toISOString().split('T')[0], // Fecha actual
				Document_Date: new Date().toISOString().split('T')[0], // Fecha actual
				// Incluir referencia a la factura original
				Applies_to_Doc_Type: "Invoice",
				Applies_to_Doc_No: invoice.No,
				lines: creditMemoLines,
				// Incluir líneas de pago automáticamente calculadas
				paymentLines: creditMemoPaymentLines,
				// Guardar las formas de pago originales de la factura para referencia
				originalPaymentMethods:
					completeInvoice.paymentLines?.map((line) => line.Payment_Method) ||
					[],
				// Indicar si la factura original tenía múltiples formas de pago (para no aplicar descuentos)
				hadMultiplePaymentMethods: hadMultiplePaymentMethods,
			};

			// Detener el escáner QR completamente antes de mostrar el formulario
			setQrModalVisible(false);
			setIsQrReaderActive(false);

			// Esperar un poco para asegurar que el escáner se ha detenido
			setTimeout(() => {
				setSelectedInvoice(creditMemo);
				setShowForm(true);
				window.messageApi.success(
					`${isCreditMemoMode ? "Abono" : "Nota de crédito"} ${isCreditMemoMode ? "creado" : "creada"
					} a partir de la factura ${no}`
				);
			}, 100);
		} catch (error) {
			console.error("Error al procesar la factura:", error);
			let mensaje = formatAxiosError(error, "No se pudo procesar la factura");
			window.messageApi.error(
				mensaje,
				{ modal: true }
			);
		}
	};

	const handleCloseQrModal = () => {
		setQrModalVisible(false);
		setIsQrReaderActive(false);
		setErrorCount(0);
		setCameraError(false);
	};

	return (
		<div className="invoice-management-container">
			{showForm ? (
				<InvoiceForm
					invoice={selectedInvoice}
					onSave={handleInvoiceSaved}
					onCancel={handleCancel}
				/>
			) : (
				<React.Fragment>
					<Row
						justify="space-between"
						align="middle"
						style={{ marginBottom: 16 }}
					>
						<Col>
							<h1>
								{isPendingMode
									? dateFilter === "today"
										? "Documentos pendientes de hoy"
										: "Documentos pendientes de sincronizar"
									: isInvoiceMode
										? dateFilter === "today"
											? "Facturas de hoy"
											: "Gestión de facturas"
										: dateFilter === "today"
											? "Abonos de hoy"
											: "Gestión de abonos"}
							</h1>
						</Col>
						{!isPendingMode && (
							<Col>
								{isInvoiceMode && (
									<Button
										type="primary"
										icon={<PlusOutlined />}
										onClick={handleAddInvoice}
										style={{ marginRight: 8 }}
									>
										Nueva Factura
									</Button>
								)}
								{isInvoiceMode && showNotasCredito && (
									<Button
										type="primary"
										icon={<ScanOutlined />}
										onClick={handleShowCreditMemoModal}
									>
										Crear nota de crédito
									</Button>
								)}
								{isCreditMemoMode && showNotasCredito && (
									<Button
										type="primary"
										icon={<ScanOutlined />}
										onClick={handleShowCreditMemoModal}
									>
										Crear desde factura
									</Button>
								)}
							</Col>
						)}
					</Row>

					<InvoiceList
						onEdit={handleEditInvoice}
						processInvoiceForCreditMemo={processInvoiceNo}
						documentType={documentType}
						syncStatus={syncStatus}
						dateFilter={dateFilter}
					/>
				</React.Fragment>
			)}

			<Modal
				title={
					isCreditMemoMode
						? "Crear abono desde factura"
						: "Crear nota de crédito desde factura"
				}
				open={qrModalVisible && !isPendingMode}
				onCancel={handleCloseQrModal}
				footer={null}
				width={500}
			>
				<div className="credit-memo-modal">
					<div className="qr-reader-container">
						{!cameraError && isQrReaderActive && qrModalVisible ? (
							<>
								<QrReader
									constraints={{ facingMode: "environment" }}
									onResult={handleQrScan}
									style={{ width: "100%" }}
									scanDelay={1000}
								/>
								<div className="scanner-overlay-text">
									<p>Escanee el código de barras o QR de la factura</p>
								</div>
							</>
						) : (
							<div className="camera-error">
								<p>
									No se pudo acceder a la cámara o ha habido demasiados errores.
									Por favor, introduzca el número manualmente.
								</p>
							</div>
						)}
					</div>

					<div className="manual-input-container">
						<p>O introduzca el número o ID de factura manualmente:</p>
						<p className="input-help-text">
							Puede introducir el número de factura o el ID con o sin ceros a la
							izquierda
						</p>
						<Input
							value={invoiceNo}
							onChange={(e) => setInvoiceNo(e.target.value)}
							placeholder="Número o ID de factura"
							style={{ marginBottom: 16 }}
						/>
						<Button
							type="primary"
							onClick={handleManualInput}
							loading={loading}
						>
							Aceptar
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default InvoiceManagement;
